import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Lock, MessageCircle, Zap, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AttachmentButton } from "@/components/inbox/AttachmentButton";
import { VoiceRecorder } from "@/components/inbox/VoiceRecorder";
import { TemplateDialog } from "@/components/inbox/TemplateDialog";

export const Composer = ({ conversation: c }) => {
  const qc = useQueryClient();
  const [mode, setMode] = useState("reply");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [hi, setHi] = useState(0);
  const [recording, setRecording] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["messages", c.id] });
    qc.invalidateQueries({ queryKey: ["conversations"] });
    qc.invalidateQueries({ queryKey: ["conversation", c.id] });
  };
  const { data: replies = [] } = useQuery({ queryKey: ["quick-replies"], queryFn: () => api.get("/quick-replies").then((r) => r.data) });

  const slash = text.startsWith("/") && !text.includes(" ") ? text.slice(1).toLowerCase() : null;
  const matches = useMemo(
    () => (slash === null ? [] : replies.filter((r) => r.shortcut.includes(slash) || r.title.toLowerCase().includes(slash))),
    [slash, replies]
  );

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${c.id}/messages`, { text, type: mode });
      setText("");
      if (data.delivery === "failed") toast.error(`Invio WhatsApp fallito: ${data.error}`);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  const pick = (r) => { setText(r.text); setHi(0); };

  const onKey = (e) => {
    if (matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHi((hi + 1) % matches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setHi((hi - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[hi] || matches[0]); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const note = mode === "note";
  const closed = c.status === "closed";
  const expired = !c.window_open;
  const locked = closed || (!note && expired);
  return (
    <div className={`shrink-0 border-t px-3 md:px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] relative transition-colors ${note ? "bg-[#FEFCE8] border-[#FDE047]" : "bg-white border-slate-200"}`}>
      {!note && expired && (
        <div data-testid="window-expired-banner" className="flex items-center gap-2 text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-2.5">
          <Clock size={14} className="shrink-0" />
          <span>La finestra di 24 ore è scaduta: per scrivere al cliente invia un <b>modello approvato da Meta</b>. Puoi comunque aggiungere note interne.</span>
        </div>
      )}
      {matches.length > 0 && (
        <div data-testid="quick-reply-popover" className="absolute bottom-full left-5 right-5 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden fade-up">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-400 border-b border-slate-100 flex items-center gap-1.5"><Zap size={11} /> Risposte rapide</div>
          {matches.map((r, i) => (
            <button key={r.id} data-testid={`quick-reply-option-${r.shortcut}`} onMouseDown={(e) => { e.preventDefault(); pick(r); }} className={`w-full text-left px-3 py-2 flex gap-3 items-start ${i === hi ? "bg-slate-100" : "hover:bg-slate-50"}`}>
              <code className="text-xs font-semibold text-[#15803D] bg-[#DCFCE7] rounded px-1.5 py-0.5 shrink-0">/{r.shortcut}</code>
              <div className="min-w-0"><div className="text-sm font-semibold text-slate-800">{r.title}</div><div className="text-xs text-slate-500 truncate">{r.text}</div></div>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1 mb-2.5">
        <button data-testid="mode-toggle-reply" onClick={() => setMode("reply")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${!note ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-yellow-100"}`}>
          <MessageCircle size={13} /> Rispondi
        </button>
        <button data-testid="mode-toggle-internal-note" onClick={() => setMode("note")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${note ? "bg-[#FDE047] text-[#713F12]" : "text-slate-500 hover:bg-slate-100"}`}>
          <Lock size={13} /> Nota interna
        </button>
        <span className="ml-auto text-[11px] text-slate-400 hidden sm:inline">{note ? "Visibile solo al team interno" : "Digita / per le risposte rapide · Invio per inviare"}</span>
      </div>
      <div className="flex items-end gap-1.5">
        {!note && !recording && (
          <>
            <TemplateDialog conversation={c} highlight={expired && !closed} onSent={refresh} />
            <AttachmentButton cid={c.id} disabled={locked} onSent={refresh} />
          </>
        )}
        {!note && !locked && <VoiceRecorder cid={c.id} disabled={locked} onSent={refresh} onRecording={setRecording} />}
        {!recording && <textarea
          data-testid="chat-input-textarea"
          rows={2}
          value={text}
          onChange={(e) => { setText(e.target.value); setHi(0); }}
          onKeyDown={onKey}
          disabled={locked}
          placeholder={closed ? "Chat chiusa: riaprila per rispondere" : note ? "Scrivi una nota per il team…" : expired ? "Finestra 24h scaduta: usa un modello" : "Scrivi un messaggio…"}
          className={`flex-1 resize-none rounded-xl px-4 py-2.5 text-sm outline-none border transition-colors ${note ? "bg-[#FEF9C3] border-[#FDE047] focus:ring-2 focus:ring-yellow-300" : "bg-slate-50 border-slate-200 focus:bg-white focus:ring-2 focus:ring-[#25D366]/40"}`}
        />}
        {!recording && <button
          data-testid="chat-send-button"
          onClick={send}
          disabled={!text.trim() || sending || locked}
          className={`h-11 w-11 rounded-full grid place-items-center transition-[transform,background-color] active:scale-95 disabled:opacity-40 ${note ? "bg-[#EAB308] text-white" : "bg-[#25D366] hover:bg-[#22C55E] text-white"}`}
          title={note ? "Salva nota" : "Invia"}
        >
          {note ? <Lock size={17} /> : <Send size={17} />}
        </button>}
      </div>
    </div>
  );
};
